/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface ManifestEntryProps {
  id: string;
  passengerId: string;
  confirmedAt: Date | null;
  confirmedById: string | null;
  tripLegId: string;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class ManifestEntryRecord extends Record<ManifestEntryProps>({
  id: null as any,
  passengerId: null as any,
  confirmedAt: null as any,
  confirmedById: null as any,
  tripLegId: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    passengerId: string;
    confirmedAt: string | number | Date | null;
    confirmedById: string | null;
    tripLegId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): ManifestEntryRecord {
    if (json == null) {
      throw new Error('ManifestEntryRecord.fromJS: parameter cannot be null');
    }
    return new ManifestEntryRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'ManifestEntryJson.id',
      ),
      passengerId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'passengerId', '00000000-0000-0000-0000-000000000000'),
        'ManifestEntryJson.passengerId',
      ),
      confirmedAt: json.confirmedAt != null ? new Date(json.confirmedAt) : null,
      confirmedById: json.confirmedById,
      tripLegId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'tripLegId', '00000000-0000-0000-0000-000000000000'),
        'ManifestEntryJson.tripLegId',
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'ManifestEntryJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    passengerId: string;
    confirmedAt: string | number | Date | null;
    confirmedById: string | null;
    tripLegId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [ManifestEntryRecord, Error[]] {
    if (json == null) {
      throw new Error('ManifestEntryRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new ManifestEntryRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'ManifestEntryJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      passengerId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'passengerId', '00000000-0000-0000-0000-000000000000'),
            'ManifestEntryJson.passengerId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      confirmedAt: tryOrDefault(
        () => (json.confirmedAt != null ? new Date(json.confirmedAt) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      confirmedById: tryOrDefault(
        () => json.confirmedById,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      tripLegId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'tripLegId', '00000000-0000-0000-0000-000000000000'),
            'ManifestEntryJson.tripLegId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'ManifestEntryJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: ManifestEntryProps) {
    super(props);
    if (props == null) {
      throw new Error('ManifestEntryRecord constructor parameter cannot be null');
    }
  }
}

export abstract class ManifestEntryViewModelBase implements ManifestEntryProps {
  protected readonly _props: ManifestEntryProps;
  private _json?: ManifestEntryProps;

  get id(): string {
    return this._props.id;
  }
  get passengerId(): string {
    return this._props.passengerId;
  }
  get confirmedAt(): Date | null {
    return this._props.confirmedAt;
  }
  get confirmedById(): string | null {
    return this._props.confirmedById;
  }
  get tripLegId(): string {
    return this._props.tripLegId;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: ManifestEntryProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        passengerId: this._props.passengerId,
        confirmedAt: this._props.confirmedAt,
        confirmedById: this._props.confirmedById,
        tripLegId: this._props.tripLegId,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllManifestEntryEntities = (state: AppState) =>
  state.entityStores.manifestEntry.entities;

export const selectManifestEntryById = (id: string) => (state: AppState) =>
  state.entityStores.manifestEntry.entities.get(id);

export const selectManifestEntryMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.manifestEntry.meta.get(id);

export const selectManifestEntryAndMetadataById = (id: string) =>
  createSelector(
    selectManifestEntryById(id),
    selectManifestEntryMetadataById(id),
    (manifestEntry, meta) => ({ manifestEntry, meta }),
  );

export function selectManifestEntryEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllManifestEntryEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, ManifestEntryRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectManifestEntryEntitiesByPassengerId(passengerId: string) {
  return createSelector(selectAllManifestEntryEntities, (entities) =>
    entities.filter((e) => e.passengerId === passengerId).valueSeq(),
  );
}

export function selectManifestEntryEntitiesByConfirmedAt(confirmedAt: Date) {
  return createSelector(selectAllManifestEntryEntities, (entities) =>
    entities.filter((e) => e.confirmedAt === confirmedAt).valueSeq(),
  );
}

export function selectManifestEntryEntitiesByConfirmedById(confirmedById: string) {
  return createSelector(selectAllManifestEntryEntities, (entities) =>
    entities.filter((e) => e.confirmedById === confirmedById).valueSeq(),
  );
}

export function selectManifestEntryEntitiesByTripLegId(tripLegId: string) {
  return createSelector(selectAllManifestEntryEntities, (entities) =>
    entities.filter((e) => e.tripLegId === tripLegId).valueSeq(),
  );
}

export function selectManifestEntryEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllManifestEntryEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
